import type { ICalculateMetricsInputModel, ILegalEntitySliceMeta } from '@foresightdata/metrics-calculator';
import { IFundDataModel } from '../../../data-models/fund.data-model';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import {
  ILegalStructureGraphResponse,
  ILegalStructureNodeRef,
} from '../../../services/queries/MaggieLegalStructureQueries';

export type PortfolioTreeMode = 'rollup' | 'individual';
export type PortfolioTreeNodeKind = 'family' | 'fund' | 'vehicle' | 'company';
export type PortfolioTreeSource = 'ledger' | 'allocated' | 'estimated';

/** Weakest provenance wins: a parent is only as booked as the least booked thing it pools. */
const SOURCE_RANK: Record<PortfolioTreeSource, number> = { ledger: 2, allocated: 1, estimated: 0 };
export const weaker = (a: PortfolioTreeSource, b: PortfolioTreeSource) =>
  SOURCE_RANK[b] < SOURCE_RANK[a] ? b : a;

export interface IPortfolioTreeNode {
  key: string;
  kind: PortfolioTreeNodeKind;
  label: string;
  path: string[];
  nodeRef: ILegalStructureNodeRef;
  incomplete: boolean;
  source: PortfolioTreeSource;
}

export interface IPortfolioTreeInput {
  graphs: ILegalStructureGraphResponse[];
  metrics: IMetricsDataModel[];
  fundsById: Map<number, IFundDataModel>;
  mode: PortfolioTreeMode;
}

export interface IPortfolioTreeLeaf {
  node: IPortfolioTreeNode;
  slice: ICalculateMetricsInputModel & ILegalEntitySliceMeta;
}

export interface IPortfolioTreeBuild {
  nodes: IPortfolioTreeNode[];
  leaves: IPortfolioTreeLeaf[];
  leafKeysByNode: Map<string, string[]>;
  /** Vehicles with no ledger slice beneath them, under every node whose pool carries their register
   * value. At most one entry per entity per node: the register carries one figure per entity. */
  leaflessVehiclesByNode: Map<string, string[]>;
}
